import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) { navigate('/', { replace: true }); return; }
    api.getUsers()
      .then(data => setUsers(data.users))
      .catch(() => setMessage({ type: 'error', text: 'Failed to load users.' }))
      .finally(() => setLoadingUsers(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user?.isAdmin) return null;

  const handleToggleTier = async (userId, currentTier) => {
    const newTier = currentTier === 'premium' ? 'free' : 'premium';
    try {
      const { user: updated } = await api.setUserTier(userId, newTier);
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, tier: updated.tier } : u));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setSending(true);
    setMessage(null);
    try {
      const data = await api.sendTestInvite(inviteEmail.trim());
      setMessage({ type: 'success', text: data.message });
      setInviteEmail('');
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 px-6 pb-16">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-serif text-2xl text-warm mb-8">Admin Console</h1>

        {/* Send Test Invite */}
        <div className="border border-dark-200/50 rounded-xl p-5 mb-8">
          <h2 className="text-warm-100/60 text-xs uppercase tracking-wider mb-4">Invite</h2>
          <form onSubmit={handleSendInvite} className="flex gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 bg-dark-100 border border-dark-200/50 rounded-lg px-4 py-2 text-sm text-warm placeholder-warm-100/30 focus:outline-none focus:border-gold/40"
              required
            />
            <button
              type="submit"
              disabled={sending}
              className="bg-gold/15 border border-gold/30 text-gold px-5 py-2 rounded-lg text-sm hover:bg-gold/25 transition-colors disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Invite'}
            </button>
          </form>
          {message && (
            <p className={`mt-3 text-sm ${message.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
              {message.text}
            </p>
          )}
        </div>

        {/* Users List */}
        <div className="border border-dark-200/50 rounded-xl p-5">
          <h2 className="text-warm-100/60 text-xs uppercase tracking-wider mb-4">
            Users {!loadingUsers && <span className="text-warm-100/30">({users.length})</span>}
          </h2>
          {loadingUsers ? (
            <p className="text-warm-100/40 text-sm">Loading...</p>
          ) : users.length === 0 ? (
            <p className="text-warm-100/40 text-sm">No users yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-warm-100/40 text-xs uppercase tracking-wider border-b border-dark-200/30">
                    <th className="text-left py-2 pr-4">Name</th>
                    <th className="text-left py-2 pr-4">Email</th>
                    <th className="text-left py-2 pr-4">Tier</th>
                    <th className="text-left py-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u._id} className="border-b border-dark-200/20">
                      <td className="py-3 pr-4 text-warm">{u.name || '—'}</td>
                      <td className="py-3 pr-4 text-warm-100/60">{u.email}</td>
                      <td className="py-3 pr-4">
                        <button
                          onClick={() => handleToggleTier(u._id, u.tier)}
                          className={`px-2 py-0.5 rounded-full text-xs cursor-pointer hover:opacity-80 transition-opacity ${u.tier === 'premium' ? 'bg-gold/20 text-gold' : 'bg-dark-200 text-warm-100/60'}`}
                          title={`Click to set ${u.tier === 'premium' ? 'free' : 'premium'}`}
                        >
                          {u.tier}
                        </button>
                      </td>
                      <td className="py-3 text-warm-100/40">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
